import { useEffect, useState } from 'react';
import {
  Controller,
  FormProvider,
  SubmitHandler,
  useFieldArray,
  useForm,
} from 'react-hook-form';

import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import { Button, Divider, Grid, IconButton, Stack } from '@mui/material';

import { DataApi } from '@/api';
import { FormCard, RenderFormField } from '@/components';
import { IIncidentCode, IIncidentCodePayload, IUnitType } from '@/models';
import { ConfirmTypes, useAgencyStore, useToastStore } from '@/store';

export interface IncidentCodeFormValues {
  code: string;
  emergency: boolean;
  useForCarStop: boolean;
  carStopAgencies: string[];
  description?: string;
  requiredUnits?: Array<{ unitType: string; number: number; _id?: string }>;
}

interface IncidentCodeFormProps {
  formId?: string;
  onSubmit: (data: IIncidentCodePayload) => Promise<any>;
  initialData?: IIncidentCode;
  isEditMode?: boolean;
  agencyId?: string;
}

const IncidentCodeFormFields = {
  title: 'Incident code details',
  items: [
    {
      name: 'code',
      label: 'Incident Code',
      type: 'text',
      required: true,
      grid: { xs: 12, md: 6 },
      maxLength: 50,
      helperText: 'Maximum 50 characters',
    },
    {
      name: 'description',
      label: 'Description',
      type: 'text',
      grid: { xs: 12, md: 6 },
      maxLength: 255,
      helperText: 'Maximum 255 characters',
    },
  ],
};

export const IncidentCodeForm = ({
  formId,
  onSubmit,
  initialData,
  isEditMode = false,
}: IncidentCodeFormProps) => {
  const { updateToast } = useToastStore();
  const { agencies, fetchAgencies } = useAgencyStore();
  const [unitTypes, setUnitTypes] = useState<IUnitType[]>([]);
  const [loadingUnitTypes, setLoadingUnitTypes] = useState<boolean>(true);
  const [loadingAgencies, setLoadingAgencies] = useState<boolean>(true);

  const sortedAgencies = [...agencies].sort((a, b) =>
    a.name.localeCompare(b.name),
  );
  const singleAgency = sortedAgencies.length === 1 ? sortedAgencies[0] : null;
  const agencySelectItems = sortedAgencies.map(({ _id, name }) => ({
    label: name,
    value: _id,
  }));

  const methods = useForm<IncidentCodeFormValues>({
    defaultValues: {
      code: '',
      description: '',
      emergency: true,
      useForCarStop: false,
      carStopAgencies: [],
      requiredUnits: [],
    },
    mode: 'onBlur',
    resolver: undefined,
    context: undefined,
    criteriaMode: 'firstError',
    shouldFocusError: true,
    shouldUnregister: false,
    shouldUseNativeValidation: false,
  });

  const { control, handleSubmit: RHFhandleSubmit, reset, watch } = methods;
  const {
    formState: { isSubmitting },
  } = methods;

  const { fields, append, remove } = useFieldArray<
    IncidentCodeFormValues,
    'requiredUnits'
  >({
    control,
    name: 'requiredUnits',
  });

  // Watch for changes to required units to filter already selected unit types
  const selectedUnitTypes =
    watch('requiredUnits')?.map((unit) => unit.unitType) || [];
  const useForCarStop = watch('useForCarStop');

  useEffect(() => {
    const loadAgencies = async () => {
      if (agencies.length) {
        setLoadingAgencies(false);
        return;
      }

      try {
        setLoadingAgencies(true);
        await fetchAgencies();
      } catch (error) {
        updateToast({
          type: ConfirmTypes.ERROR,
          message: 'Failed to load agencies for Car Stop configuration.',
          open: true,
        });
      } finally {
        setLoadingAgencies(false);
      }
    };

    loadAgencies();
  }, [agencies.length, fetchAgencies, updateToast]);

  useEffect(() => {
    const fetchUnitTypes = async () => {
      try {
        setLoadingUnitTypes(true);
        const response = await DataApi.getUnitTypes(true);
        setUnitTypes(response.data || []);
      } catch (error) {
        updateToast({
          type: ConfirmTypes.ERROR,
          message: 'Failed to load unit types for selection.',
          open: true,
        });
        setUnitTypes([]);
      } finally {
        setLoadingUnitTypes(false);
      }
    };
    fetchUnitTypes();
  }, [updateToast]);

  useEffect(() => {
    if (initialData && unitTypes.length > 0) {
      const carStopAgencies = initialData.carStopAgencies ?? [];
      const formValues = {
        code: initialData.code,
        description: initialData.description || '',
        emergency: initialData.emergency,
        useForCarStop: carStopAgencies.length > 0,
        carStopAgencies,
        requiredUnits:
          initialData.requiredUnits?.map((ru) => {
            // Extract unit type ID whether it's an object or string
            const unitTypeId =
              typeof ru.unitType === 'object' ? ru.unitType._id : ru.unitType;

            return {
              unitType: unitTypeId,
              number: ru.number,
              _id: ru._id,
            };
          }) || [],
      };

      reset(formValues);
    } else if (initialData) {
      const carStopAgencies = initialData.carStopAgencies ?? [];
      reset({
        code: initialData.code,
        description: initialData.description || '',
        emergency: initialData.emergency,
        useForCarStop: carStopAgencies.length > 0,
        carStopAgencies,
        requiredUnits: [],
      });
    }
  }, [initialData, reset, singleAgency?._id, unitTypes]);

  const handleFormSubmit: SubmitHandler<IncidentCodeFormValues> = async (
    data,
  ) => {
    const carStopAgencies = !data.useForCarStop
      ? []
      : singleAgency?._id
      ? [singleAgency._id]
      : data.carStopAgencies || [];
    const payload: IIncidentCodePayload = {
      code: data.code,
      description: data.description,
      emergency: data.emergency,
      carStopAgencies: Array.from(new Set(carStopAgencies)),
      requiredUnits:
        data.requiredUnits?.map((ru) => ({
          unitType: ru.unitType,
          number: ru.number,
        })) || [],
    };
    try {
      await onSubmit(payload);
    } catch (error: any) {
      updateToast({
        type: ConfirmTypes.ERROR,
        message:
          error.message ||
          `Submission failed: ${
            isEditMode ? 'updating' : 'creating'
          } incident code.`,
        open: true,
      });
    }
  };

  // Generate a filtered list of available unit types, excluding already selected ones
  const getAvailableUnitTypes = (currentIndex: number) => {
    // Get all selected unit types except the current one being edited
    const otherSelectedTypes = selectedUnitTypes.filter(
      (_, idx) => idx !== currentIndex,
    );

    // Get the current unit type ID for this index
    const currentUnitTypeId = selectedUnitTypes[currentIndex];

    // Filter out unit types that are already selected in other rows
    return unitTypes
      .filter((unitType) => {
        const unitTypeId = unitType._id || '';
        return (
          !otherSelectedTypes.includes(unitTypeId) ||
          unitTypeId === currentUnitTypeId
        );
      })
      .map(({ _id, name }) => ({
        label: name,
        value: _id || '',
      }));
  };

  // Create options for number of units dropdown (1-10)
  const numberOptions = Array.from({ length: 10 }, (_, i) => ({
    label: String(i + 1),
    value: i + 1,
  }));

  return (
    <FormProvider {...methods}>
      <form id={formId} onSubmit={RHFhandleSubmit(handleFormSubmit)} noValidate>
        <Grid spacing={4} container>
          <Grid xs={12} item>
            <FormCard title={IncidentCodeFormFields.title}>
              {IncidentCodeFormFields.items.map(({ grid, ...rest }: any) => (
                <Grid key={rest.name} xs={12} sm={6} md={6} {...grid} item>
                  <RenderFormField
                    {...rest}
                    rules={
                      rest.name === 'code'
                        ? {
                            required: 'Incident Code is required',
                            maxLength: {
                              value: 50,
                              message:
                                'Incident Code cannot exceed 50 characters',
                            },
                          }
                        : rest.name === 'description'
                        ? {
                            maxLength: {
                              value: 255,
                              message:
                                'Description cannot exceed 255 characters',
                            },
                          }
                        : undefined
                    }
                  />
                </Grid>
              ))}
              <Grid xs={12} sm={6} md={6} item>
                <RenderFormField
                  name="emergency"
                  label="Auto Ready for Dispatch"
                  type="switch"
                />
              </Grid>
              <Grid xs={12} sm={6} md={6} item>
                <RenderFormField
                  name="useForCarStop"
                  label="Use for Car Stop"
                  type="switch"
                  disabled={loadingAgencies}
                />
              </Grid>
              {!singleAgency && useForCarStop && (
                <Grid xs={12} item>
                  <RenderFormField
                    name="carStopAgencies"
                    label="Car Stop Agencies"
                    type="autocomplete"
                    items={agencySelectItems}
                    multiple
                    limitTags={2}
                    disabled={loadingAgencies}
                    rules={{
                      required: 'Car Stop Agencies is required',
                      validate: (value: string[]) =>
                        value?.length > 0 || 'Car Stop Agencies is required',
                    }}
                  />
                </Grid>
              )}
            </FormCard>
          </Grid>

          <Grid xs={12} item>
            <FormCard title="Required units">
              {fields.map((item, index) => (
                <Grid xs={12} item key={item.id}>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={2}
                    sx={{ mb: 2, alignItems: 'flex-start' }}
                  >
                    <Grid xs={12} sm={6} item>
                      <Controller
                        name={`requiredUnits.${index}.unitType`}
                        control={control}
                        rules={{ required: 'Unit Type is required' }}
                        render={({ field }) => (
                          <RenderFormField
                            type="select"
                            name={`requiredUnits.${index}.unitType`}
                            label="Unit Type"
                            required
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            disabled={loadingUnitTypes || isSubmitting}
                            items={
                              loadingUnitTypes
                                ? [{ label: 'Loading...', value: '' }]
                                : unitTypes.length === 0
                                ? [
                                    {
                                      label: 'No unit types available',
                                      value: '',
                                    },
                                  ]
                                : getAvailableUnitTypes(index)
                            }
                          />
                        )}
                      />
                    </Grid>

                    <Grid xs={12} sm={4} item>
                      <Controller
                        name={`requiredUnits.${index}.number`}
                        control={control}
                        rules={{
                          required: 'Number of units is required',
                        }}
                        render={({ field }) => (
                          <RenderFormField
                            type="select"
                            label="Number of Units"
                            name={`requiredUnits.${index}.number`}
                            required
                            value={String(field.value)}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            disabled={isSubmitting}
                            items={numberOptions}
                          />
                        )}
                      />
                    </Grid>

                    <Grid
                      xs={12}
                      sm={2}
                      item
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-end',
                        justifyContent: 'flex-start',
                        height: '100%',
                        pt: 2.5,
                      }}
                    >
                      <IconButton
                        onClick={() => remove(index)}
                        color="error"
                        disabled={isSubmitting}
                        aria-label="Remove required unit"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Grid>
                  </Stack>
                </Grid>
              ))}

              <Grid xs={12} item>
                <Divider sx={{ my: 2 }} />
                <Button
                  startIcon={<AddCircleOutlineIcon />}
                  onClick={() =>
                    append({ unitType: '', number: 1, _id: undefined })
                  }
                  disabled={
                    isSubmitting ||
                    loadingUnitTypes ||
                    selectedUnitTypes.length >= unitTypes.length
                  }
                  variant="text"
                >
                  Add unit type
                </Button>
              </Grid>
            </FormCard>
          </Grid>
        </Grid>
      </form>
    </FormProvider>
  );
};
